import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCarStatusHistory } from '../../../api/carsApi';
import { StatusHistoryItem } from '../../../interfaces/CarStatusHistory';
import { ApprovalStatusMap } from '../../mappings/textMappings';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';

import './StatusHistoryModal.css';

interface StatusHistoryModalProps {
  isOpen: boolean;
  carId: string;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
}

const StatusHistoryModal: React.FC<StatusHistoryModalProps> = ({
  isOpen,
  carId,
  onCancel,
  sidebarRef
}) => {
  const [statusHistory, setStatusHistory] = useState<StatusHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const limit = 10;
  const modalRef = useRef<HTMLDivElement>(null);

  const fetchStatusHistory = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError('');
      const response = await getCarStatusHistory(carId, page, limit);
      setStatusHistory(response.history);
      setTotalPages(Math.ceil(response.total / limit));
      setLoading(false);
    } catch (err) {
      setError('Erro ao carregar o histórico de esteiras');
      setLoading(false);
    }
  }, [carId, limit]);

  useEffect(() => {
    if (isOpen) {
      fetchStatusHistory(currentPage);
    }
  }, [isOpen, currentPage, fetchStatusHistory]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  if (!isOpen) {
    return null;
  }

  function formatHoursAndSeconds(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  return (
    <div className="add-car-modal-overlay modal-overlay--empilhado">
      <div className="parts-history-modal" ref={modalRef}>
        <div className="parts-history-title">Histórico de Esteiras</div>

        <div className="parts-history-grid">
          <div className="parts-history-row parts-history-row--6 parts-history-header">
            <div>Esteira</div>
            <div>Status de Aprovação</div>
            <div>Alterado</div>
            <div>Tempo no Status</div>
            <div>Data de Atribuição</div>
            <div>Válido Até</div>
          </div>

          {loading && <div className="parts-history-empty">Carregando histórico...</div>}
          {!loading && error && <div className="parts-history-empty">{error}</div>}
          {!loading && !error && statusHistory.length === 0 && (
            <div className="parts-history-empty">Sem histórico de esteiras para este veículo.</div>
          )}

          {!loading && !error && statusHistory.map((status, index) => (
            <div className="parts-history-row parts-history-row--6" key={`${status.changeDate}-${status.status}-${index}`}>
              <div>{status.statusLabel || status.status}</div>
              <div>{ApprovalStatusMap[status.approvalStatus] || status.approvalStatus}</div>
              <div>{status.changedByName}</div>
              <div>
                {status.secondsInStatus !== null && Number(status.secondsInStatus) !== 0
                  ? formatHoursAndSeconds(Number(status.secondsInStatus))
                  : 'N/A'}
              </div>
              <div>{new Date(status.changeDate).toLocaleString()}</div>
              <div>{status.validTo ? new Date(status.validTo).toLocaleString() : 'Atual'}</div>
            </div>
          ))}
        </div>

        <div className="parts-history-pagination">
          <div
            className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={currentPage > 1 ? handlePreviousPage : undefined}
            style={{
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              pointerEvents: currentPage === 1 ? 'none' : 'auto',
            }}
            aria-disabled={currentPage === 1}
          >
            <img
              className="imgIcon"
              src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
              alt="Seta para Esquerda"
              width="24"
              height="24"
            />
            <div
              className="Anterior"
              style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
            >
              Anterior
            </div>
          </div>

          <div className="Lta2456">Página {currentPage} de {totalPages}</div>

          <div
            className={`Frame1000003949 ${currentPage >= totalPages ? 'disabled' : ''}`}
            onClick={currentPage < totalPages ? handleNextPage : undefined}
            style={{
              cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            }}
          >
            <div
              className="PrXima"
              style={{ color: currentPage >= totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
            >
              Próxima
            </div>
            <img
              className="imgIcon"
              src={currentPage >= totalPages ? ArrowRightGray : ArrowRightOrange}
              alt="Seta para Direita"
              width="24"
              height="24"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusHistoryModal;
